/**
 * ComposeRecipientSection Component
 * Simplified: Label + Gmail-style search with inline badges
 */

import { SPACING } from '@/design-system';
import { LComponents } from '@/components/i18n';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ComposeExpertSearch } from './ComposeExpertSearch';
import type { Expert } from './types';

interface ComposeRecipientSectionProps {
  selectedExperts: Expert[];
  onAddExpert: (expert: Expert) => void;
  onRemoveExpert: (expertId: string) => void;
  recentContacts: Expert[] | undefined;
  locationMatches: Expert[] | undefined;
  locationMatchesMode?: 'top_experts' | 'near_you';
  experts: Expert[] | undefined;
  expertsLoading: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  lockedRecipients?: boolean;
  context?: string;
  recipientError?: string;
}

export function ComposeRecipientSection({
  selectedExperts,
  onAddExpert,
  onRemoveExpert,
  recentContacts,
  locationMatches,
  locationMatchesMode,
  experts,
  expertsLoading,
  searchQuery,
  onSearchChange,
  lockedRecipients,
  context,
  recipientError,
}: ComposeRecipientSectionProps) {
  // Don't show recipient controls in tax-oracle context
  if (context === 'tax-oracle') {
    return null;
  }

  return (
    <div className={SPACING.stackSm}>
      <div className="flex items-center justify-between">
        <Label className="text-sm">
          <LComponents id="compose_message.labels.to" defaultValue='To' />
        </Label>
        {selectedExperts.length > 0 && (
          <Badge variant="secondary" className="text-xs font-normal">
            {selectedExperts.length}/3
          </Badge>
        )}
      </div>
      {/* Gmail-style search with inline badges */}
      <ComposeExpertSearch
        experts={experts}
        expertsLoading={expertsLoading}
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        selectedExperts={selectedExperts}
        onAddExpert={onAddExpert}
        onRemoveExpert={onRemoveExpert}
        lockedRecipients={lockedRecipients}
        disabled={false}
        recentContacts={recentContacts}
        locationMatches={locationMatches}
        locationMatchesMode={locationMatchesMode}
      />
      {/* Recipient Error */}
      {recipientError && (
        <p className="text-sm text-destructive">{recipientError}</p>
      )}
    </div>
  );
}
